import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Flex, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';

import Layout from '../../../../components/layout';
import Breadcrumb from '../../../../components/common/breadcrumb';
import JobTabs from '../../components/job-tabs';
import InterviewerTab from '../../components/interviewer-setup/InterviewerTab';
import { postingFrom } from '../../interviewer/model';
import type { Job } from '../../../../fsm/jobs';
import type { User } from '../../../../fsm/users';
import styles from './index.module.css';

interface Props {
  user?: User;
  data: Job | null;
  onFetchDetails: (id: number) => void;
}

/**
 * A job's Interviewer tab: how Rio interviews candidates for this job, and
 * where the recruiter changes it. Routed only with the interviewer setup on.
 */
const JobInterviewer = ({ user, data, onFetchDetails }: Props) => {
  const { jobId } = useParams();
  const id = jobId ? Number(jobId) : NaN;

  useEffect(() => {
    if (Number.isFinite(id) && data?.id !== id) onFetchDetails(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const posting = useMemo(
    () =>
      postingFrom({
        title: data?.title,
        description: data?.description,
        requiredSkills: data?.requiredSkills,
        preferredSkills: data?.preferredSkills,
      }),
    [data],
  );

  const ready = data && data.id === id;

  return (
    <Layout user={user}>
      <Breadcrumb
        renderLeft={
          <Button type="text" icon={<ArrowLeftOutlined />}>
            <Link to="/jobs">Back to job list</Link>
          </Button>
        }
      />
      <div className={styles.header}>
        <p className={styles.eyebrow}>Job</p>
        <h1 className={styles.title}>{ready ? data.title : 'Loading…'}</h1>
      </div>
      <JobTabs jobId={jobId ?? ''} />
      <div className={styles.body}>
        {ready ? (
          <InterviewerTab jobId={id} posting={posting} />
        ) : (
          <Flex align="center" justify="center" className={styles.loading}>
            <Spin tip="Loading…" />
          </Flex>
        )}
      </div>
    </Layout>
  );
};

export default JobInterviewer;
